import { FALLBACK_LOCALE, getCurrentLocaleByCode } from '~/i18n';

import { setCookie } from './useCookie';
import { ILocale } from '~/locales';
import { useRouter } from 'next/router';
import { getQueryString } from '~/utils/client-utils';
import { COOKIE_LOCALE } from '~/constants';
import { formatHref } from '~/components/atoms/Link/Link';

export function useLocale() {
  const nextRouter = useRouter();
  const pathname = nextRouter.asPath.split('?')[0];
  const search = getQueryString(nextRouter.query);
  const back = () => nextRouter.back();

  const localeCode = nextRouter.locale ?? FALLBACK_LOCALE.code;
  const currentLocale = getCurrentLocaleByCode(localeCode);

  const navigateTo = (path: string, newLocale?: string) => {
    nextRouter.push(formatHref(newLocale ?? localeCode, path), undefined, { locale: newLocale ?? localeCode });
  };

  /**
   * When the language has changed we will store the new locale in a cookie.
   */
  const setLocaleCookie = (locale: string): void => {
    setCookie(COOKIE_LOCALE, locale);
  };

  /**
   * When the user changes the language, we will send the newly selected language to the backend.
   * If the userId is null, then the user is not logged in and we will not send the language to the backend.
   *
   * @param userId
   * @param locale the new language selected
   */
  const changeUserLocale = async (
    userId: string | null,
    locale: ILocale,
  ): Promise<void> => {
    try {
      setLocaleCookie(locale.code);
      const newPath = `/${locale.code}${pathname}`;
      nextRouter.push(newPath, undefined, { locale: locale.code });
    } catch (err) {
      console.error(err);
    }
  };

  return {
    setLocaleCookie,
    changeUserLocale,
    pathname,
    currentLocale,
    search,
    back,
    nextRouter,
    navigateTo,
  };
}
